/**
 * Dashboard similar-course recommendations.
 */
import React, { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";

import { useAsterController } from "context";
import { VText, VButton } from "form";
import { BThumb } from "components";
import { getCourseImageUrl, getSimilarity } from "utils/string";

const MAX_DISPLAY_COUNT = 3;

/**
 * Lists up to three catalog courses similar to the learner's enrollments.
 */
const DashboardSimilar = () => {
  const { t } = useTranslation("common");

  const { courseId } = useParams();
  const navigate = useNavigate();

  const [controller] = useAsterController();
  const { courses, loadedDashboard } = controller;

  const [similarCourses, setSimilarCourses] = useState([]);

  useEffect(() => {
    if ((courses.data || []).length) {
      const courseData = courses.data || [];
      const enrolled = courseData.filter((c) => !!c.enrollment_status);
      const enrollCourseTitles = enrolled.map((e) => e.display_name).slice(0, MAX_DISPLAY_COUNT);

      // [{ id, title, similarity }]
      let similarTitles = [];
      let minValue = 0.0;

      courseData.forEach((course) => {
        if (similarTitles.findIndex((s) => s.id === course.id) >= 0) return;

        let maxSimilarity = 0.0;
        for (let enrollTitle of enrollCourseTitles) {
          if (enrollTitle === course.display_name) continue;

          const similarity = getSimilarity(enrollTitle, course.display_name);
          if (similarity > maxSimilarity) {
            maxSimilarity = similarity;
          }
        }

        if (minValue < maxSimilarity) {
          similarTitles.unshift({ 
            id: course.id, 
            title: course.display_name, 
            similarity: maxSimilarity 
          });
          similarTitles
            .sort((a, b) => a.similarity - b.similarity)
            .splice(MAX_DISPLAY_COUNT);
          minValue = similarTitles[0]["similarity"];
        }
      });

      const similars = courseData.filter((c) => similarTitles.find((s) => s.id === c.id));
      setSimilarCourses(similars.slice(0, MAX_DISPLAY_COUNT));
    }
  }, [courses.data, courseId]);

  const onCourseClick = (courseId) => navigate(`/course/${courseId}`);

  return (
    <>
      <Box className="mt-8">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <VText className="text-2xl">{t("Similar contents")}</VText>
          </Grid>
          {loadedDashboard
            ? similarCourses.map((course) => (
                <Grid key={`${course.course_index}${course.id}`} item xs={12} sm={6} md={4}>
                  <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 h-full flex flex-col">
                    <Box className="flex flex-col w-full">
                      <BThumb src={getCourseImageUrl(course.course_image_url)} className="w-full aspect-[9/5] rounded-md" />
                    </Box>
                    <Box className="p-4 flex flex-col gap-1 h-full">
                      <VText className="text-xl text-limit-2">{course.display_name || "-"}</VText>
                      <VText className="text-limit-1 mb-8" div>
                        {course.short_description || "-"}
                      </VText>
                      <VButton variant="contained" color="primary" className="w-full normal-case mt-auto" onClick={() => onCourseClick(course.id)} disabled={!course.enrollment_status}>
                        {t("Start Course")}
                      </VButton>
                    </Box>
                  </Box>
                </Grid>
              ))
            : [...Array(3).keys()].map((v) => (
                <Grid key={v} item xs={12} sm={6} md={4}>
                  <Box className="rounded-md border border-solid border-gray-200 dark:border-gray-500 p-4">
                    <Skeleton className="aspect-[9/5] mb-4" />
                    <Skeleton count={4} className="mt-2" />
                    <Skeleton className="h-8 mt-4" />
                  </Box>
                </Grid>
              ))}
        </Grid>
      </Box>
    </>
  );
};

export default DashboardSimilar;
